import { useMemo } from 'react'
import styled from 'styled-components'

import { pageLayoutMaxWidth } from '@syconium/little-miss-figgy'
import { Breadcrumb, Breadcrumbs } from '@syconium/little-miss-figgy/dist/components'
import { fromMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { NextLink } from '../../../../app/_components/navigation/NextLink'
import { cookieKeys } from '../../../../app/_config/Cookies.config'
import { useCookies } from '../../../../app/_providers/CookiesProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../../lib/analytics/analytics'

import { ProductGroupCategory, breadcrumbData } from './constants'

function isProductGroupCategory(value: unknown): value is ProductGroupCategory {
  return Object.values(ProductGroupCategory).includes(value as ProductGroupCategory)
}

const Wrapper = styled.div`
  padding: ${props => props.theme.spacing(3, 4)};
  ${fromMd} {
    padding: ${props => props.theme.spacing(2.5, 10)};
  }
  margin-left: auto;
  margin-right: auto;
  max-width: ${pageLayoutMaxWidth};
`

export const ProductPageBreadcrumbs: React.FC<{
  genderCategory: string | undefined
  categoryAnalyticsId: string | undefined
}> = ({ genderCategory: genderCategoryProp, categoryAnalyticsId: categoryProp }) => {
  function isCategoryKey(key: string): key is keyof typeof breadcrumbTranslations.category {
    return key in breadcrumbTranslations.category
  }
  function isSupercategoryKey(
    key: string
  ): key is keyof typeof breadcrumbTranslations.supercategory {
    return key in breadcrumbTranslations.supercategory
  }
  function isGenderKey(key: string): key is keyof typeof breadcrumbTranslations.gender {
    return key in breadcrumbTranslations.gender
  }

  const translations = useTranslation()
  const breadcrumbTranslations = translations.pages.product.breadcrumbs
  const [cookies] = useCookies([cookieKeys.stylePreference.key])
  const navGenderCookie = cookies.navGender

  //Use the gender category prop, but if it's unisex, default to the nav gender cookie.
  const gender = useMemo((): 'men' | 'women' | 'unisex' => {
    if (genderCategoryProp === 'men' || genderCategoryProp === 'women') {
      return genderCategoryProp
    }
    if (categoryProp === ProductGroupCategory.GiftCard) return 'unisex'
    if (navGenderCookie === 'women' || navGenderCookie === 'men') {
      return navGenderCookie
    }

    return 'unisex'
  }, [genderCategoryProp, navGenderCookie, categoryProp])

  const genderCategoryBreadcrumb = breadcrumbData[gender]
  const genderCategoryKey = genderCategoryBreadcrumb?.key
  const genderCategoryText =
    genderCategoryKey && isGenderKey(genderCategoryKey)
      ? breadcrumbTranslations.gender[genderCategoryKey]
      : undefined
  const genderLink = genderCategoryBreadcrumb.collection

  const category = isProductGroupCategory(categoryProp) ? categoryProp : undefined
  const categoryBreadcrumb = category ? breadcrumbData[gender].category[category] : undefined

  const categoryKey = categoryBreadcrumb?.key
  const categoryText =
    categoryKey && isCategoryKey(categoryKey)
      ? breadcrumbTranslations.category[categoryKey]
      : undefined
  const categoryLink = categoryBreadcrumb?.collection

  const supercategoryBreadcrumb = categoryBreadcrumb?.supercategory
  const supercategoryKey = supercategoryBreadcrumb?.key
  const supercategoryText =
    supercategoryKey && isSupercategoryKey(supercategoryKey)
      ? breadcrumbTranslations.supercategory[supercategoryKey]
      : undefined
  const superCategoryLink = supercategoryBreadcrumb?.collection
  return (
    <Wrapper>
      <Breadcrumbs aria-label={breadcrumbTranslations.label}>
        {genderLink && genderCategoryText && (
          <Breadcrumb
            href={genderLink}
            as={NextLink}
            {...trackEvent({
              label: 'gender breadcrumb',
              category: 'breadcrumbs',
              action: 'click',
              value: genderLink,
            })}
          >
            {genderCategoryText}
          </Breadcrumb>
        )}
        {superCategoryLink && supercategoryText && (
          <Breadcrumb
            {...trackEvent({
              label: 'supercategory breadcrumb',
              category: 'breadcrumbs',
              action: 'click',
              value: superCategoryLink,
            })}
            as={NextLink}
            href={superCategoryLink}
          >
            {supercategoryText}
          </Breadcrumb>
        )}
        {categoryLink && categoryText && (
          <Breadcrumb
            href={categoryLink}
            as={NextLink}
            {...trackEvent({
              label: 'category breadcrumb',
              category: 'breadcrumbs',
              action: 'click',
              value: categoryLink,
            })}
          >
            {categoryText}
          </Breadcrumb>
        )}
      </Breadcrumbs>
    </Wrapper>
  )
}
